import ScrollReveal from "@/components/base/ScrollReveal";

import { ACTIVE_BOOKING_URL } from "@/config/bookingLinks.js";
const reasons = [
  {
    num: "01",
    title: "Clarity over clichés",
    body: "We help you understand what's actually driving your thoughts, behaviors, and patterns. Not just how to cope, but why they're happening in the first place.",
    numColor: "#B4A6E3",
  },
  {
    num: "02",
    title: "The right therapist fit",
    body: "Not all therapists are the same. We take matching seriously. Tell us what you're looking for and we'll find someone who's a genuine fit.",
    numColor: "#7D54FC",
  },
  {
    num: "03",
    title: "Structure that moves you forward",
    body: "Every session has a purpose. Therapy here isn't open-ended. It's intentional, goal-oriented, and built around real progress.",
    numColor: "#3C6FF8",
  },
  {
    num: "04",
    title: "A modern client experience",
    body: "Easy scheduling, clear communication, and responsive support. We try to make the experience as low-friction as possible.",
    numColor: "#6B742F",
  },
];

export default function WhyChoose() {
  return (
    <section
      className="w-full py-14 md:py-20"
      style={{
        background:
          "linear-gradient(180deg, #F5F1E8 0%, #F2EDE2 40%, #F8F5EE 100%)",
      }}
    >
      <div className="w-full px-6 md:px-12 lg:px-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left: image */}
          <ScrollReveal variant="fadeLeft" delay={0}>
            <div className="relative">
              <div className="w-full h-[420px] md:h-[520px] rounded-2xl overflow-hidden">
                <img
                  src="/images/homepage/homepage-supporting.png"
                  alt="Perspective Therapy session room"
                  className="w-full h-full object-cover object-top"
                />
              </div>

              {/* Floating badge */}
              <div
                className="absolute -bottom-6 -right-4 md:-right-8 rounded-2xl p-5 md:p-6 max-w-[220px]"
                style={{
                  background: "linear-gradient(135deg, #3C6FF8 0%, #7D54FC 100%)",
                }}
              >
                {/* Purple accent line top */}
                <div
                  className="h-[2px] w-10 rounded-full mb-3"
                  style={{ background: "rgba(255,255,255,0.4)" }}
                />
                <div className="font-fraunces text-3xl font-light text-white mb-1">
                  94<span style={{ color: "rgba(255,255,255,0.65)" }}>%</span>
                </div>
                <p className="font-manrope text-[13px] text-white/88 leading-relaxed">
                  of clients report meaningful progress by their 8th session
                </p>
              </div>
            </div>
          </ScrollReveal>

          {/* Right: content */}
          <div>
            <ScrollReveal variant="fadeIn" delay={0}>
              <div className="flex items-center gap-2 mb-4">
                <div
                  className="w-5 h-[2px] rounded-full"
                  style={{
                    background: "linear-gradient(90deg, #6B742F, #7D54FC)",
                  }}
                />
                <span
                  className="font-manrope text-[13px] font-semibold tracking-widest uppercase"
                  style={{ color: "#6B742F" }}
                >
                  Why Clients Choose Perspective Therapy
                </span>
              </div>
            </ScrollReveal>

            <ScrollReveal variant="fadeUp" delay={80}>
              <h2 className="font-fraunces text-[#2B2B2B] text-4xl md:text-5xl font-light leading-snug mb-10">
                Thoughtful therapy,
                <br />
                <em className="not-italic" style={{ color: "#7D54FC" }}>
                  built for real life
                </em>
              </h2>
            </ScrollReveal>

            <div className="flex flex-col gap-8">
              {reasons.map((r, i) => (
                <ScrollReveal key={r.num} variant="fadeUp" delay={160 + i * 80}>
                  <div className="flex gap-5 relative pl-4">
                    {/* Left color accent bar */}
                    <div
                      className="absolute left-0 top-0.5 bottom-0.5 w-[2px] rounded-full"
                      style={{ background: r.numColor }}
                    />
                    <div
                      className="font-manrope text-sm font-bold pt-0.5 w-7 shrink-0"
                      style={{ color: r.numColor }}
                    >
                      {r.num}
                    </div>
                    <div>
                      <h4 className="font-fraunces text-[#2B2B2B] text-lg font-medium mb-1.5">
                        <a id={`reason-${r.num}`}>{r.title}</a>
                      </h4>
                      <p className="font-manrope text-[#2B2B2B]/78 text-[15px] leading-relaxed">
                        {r.body}
                      </p>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>

            <ScrollReveal variant="fadeUp" delay={540}>
              <a
                href={ACTIVE_BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-10 text-white font-manrope font-semibold text-[15px] px-7 py-4 rounded-full transition-colors cursor-pointer whitespace-nowrap btn-primary"
                style={{ background: "#3C6FF8" }}
                onMouseEnter={(e) =>
                  ((e.currentTarget as HTMLAnchorElement).style.background = "#2a5de6")
                }
                onMouseLeave={(e) =>
                  ((e.currentTarget as HTMLAnchorElement).style.background = "#3C6FF8")
                }
              >
                Book Now
                <i className="ri-arrow-right-line text-base"></i>
              </a>
            </ScrollReveal>
          </div>
        </div>
      </div>
    </section>
  );
}
